import React, { useEffect, useMemo, useState } from 'react';
import { DialogHeader, DialogTitle } from '../ui/dialog';
import { ResponsiveSheet } from '../ui/responsive-sheet';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import { getDeviceUIModel, sortLocations } from '../../lib/deviceUiModel';
import { isActuatable } from '../../lib/sceneActions';

const formatLocation = (loc) =>
  loc.split(/[\s_-]+/).filter(Boolean).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

/**
 * Room-grouped multi-select device picker for the scene builder — add many
 * devices in one pass instead of one at a time.
 */
const SceneDevicePicker = ({ open, devices, selectedIds, onDone, onClose }) => {
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    if (open) setSelected(new Set(selectedIds || []));
  }, [open, selectedIds]);

  const groups = useMemo(() => {
    const byLoc = new Map();
    (devices || []).filter(isActuatable).forEach((d) => {
      const loc = d.location && d.location.trim() ? d.location : 'unassigned';
      if (!byLoc.has(loc)) byLoc.set(loc, []);
      byLoc.get(loc).push(d);
    });
    return sortLocations([...byLoc.keys()]).map((loc) => [loc, byLoc.get(loc)]);
  }, [devices]);

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  return (
    <ResponsiveSheet open={open} onClose={onClose} size="lg">
        <DialogHeader className="shrink-0 px-10 pt-6 pb-4 border-b border-border">
          <DialogTitle className="text-lg font-bold text-center">Add devices</DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
          {groups.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">No controllable devices found.</p>
          ) : (
            groups.map(([loc, list]) => (
              <div key={loc} className="mb-5">
                <h3 className="text-xs font-medium text-muted-foreground uppercase px-2 mb-1.5">
                  {loc === 'unassigned' ? 'Unassigned' : formatLocation(loc)}
                </h3>
                <div className="rounded-xl border border-border/60 overflow-hidden">
                  {list.map((d) => {
                    const model = getDeviceUIModel(d);
                    const Icon = model.icon;
                    const on = selected.has(d.id);
                    return (
                      <div
                        key={d.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => toggle(d.id)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(d.id); } }}
                        className="flex items-center gap-3 w-full px-3 py-2.5 text-left border-b border-border/10 last:border-0 hover:bg-accent/50 transition-colors cursor-pointer"
                      >
                        <span className="w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0">
                          <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                        </span>
                        <span className="text-sm truncate flex-1">{d.name || d.friendly_name || d.id}</span>
                        {/* Purely visual — the row handles the toggle (avoids a double-fire). */}
                        <Checkbox checked={on} />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="shrink-0 p-4 border-t border-border">
          <Button className="w-full h-11" onClick={() => onDone([...selected])}>
            Done{selected.size > 0 ? ` (${selected.size})` : ''}
          </Button>
        </div>
    </ResponsiveSheet>
  );
};

export default SceneDevicePicker;
